import type { BlockDefinition, Condition, Diagnostic, EdgeListEntry, MarkListEntry, PatternSpecification, PieceDefinition } from "./types";

/** Evaluates a `when` expression; throws when it cannot be resolved. */
export type ConditionEvaluator = (expression: Condition) => number;

const MAX_PART_DEPTH = 16;

/**
 * The specification as drafted for one set of option values: every element
 * whose `when` is zero is removed, and every edge and mark list (outlines,
 * internal lines, allowances, inlays, overlays) is flattened to plain IDs.
 *
 * Point operations are never conditional. An element that is on but names an
 * edge, mark or piece that is off is reported (`INACTIVE_REFERENCE`); the
 * name is kept, so the usual "could not be drawn" handling applies.
 */
export function activeSpecification(specification: PatternSpecification, evaluate: ConditionEvaluator | undefined, diagnostics: Diagnostic[]): PatternSpecification {
  const cache = new Map<string, boolean>();
  const on = (when: Condition | undefined, where: string): boolean => {
    if (when === undefined || when === null) return true;
    const key = String(when);
    const cached = cache.get(key);
    if (cached !== undefined) return cached;
    let result = false;
    if (!evaluate) {
      result = false;
    } else {
      try {
        result = evaluate(key) !== 0;
      } catch (error) {
        diagnostics.push({ severity: "warning", code: "CONDITION_UNRESOLVED", message: `${where} when "${key}" could not be evaluated, so it is off: ${error instanceof Error ? error.message : String(error)}` });
        return false;
      }
    }
    cache.set(key, result);
    return result;
  };

  // Everything that is off, so references to it can be reported.
  const offEdges = new Set<string>();
  const offMarks = new Set<string>();
  const offPieces = new Set<string>();
  const blocks: Record<string, BlockDefinition> = {};
  for (const [blockId, block] of Object.entries(specification.blocks)) {
    const edges = Object.fromEntries(Object.entries(block.edges ?? {}).filter(([edgeId, edge]) => {
      const keep = on(edge?.when, `${blockId}.${edgeId}`);
      if (!keep) offEdges.add(`${blockId}.${edgeId}`);
      return keep;
    }));
    const marks = (block.marks ?? []).filter((mark) => {
      const keep = on(mark?.when, `${blockId}.${mark?.id}`);
      if (!keep) offMarks.add(`${blockId}.${mark?.id}`);
      return keep;
    });
    const derivedValues = Object.fromEntries(Object.entries(block.derivedValues ?? {}).filter(([id, definition]) => {
      return on(definition?.when, `${blockId}.${id}`);
    }));
    const pieces = Object.entries(block.pieces ?? {}).filter(([pieceId, piece]) => {
      const keep = on(piece?.when, `${blockId}.${pieceId}`);
      if (!keep) offPieces.add(`${blockId}.${pieceId}`);
      return keep;
    });
    blocks[blockId] = {
      ...block,
      ...(block.edges ? { edges } : {}),
      ...(block.marks ? { marks } : {}),
      ...(block.derivedValues ? { derivedValues } : {}),
      ...(block.pieces ? { pieces: Object.fromEntries(pieces) as Record<string, PieceDefinition> } : {}),
    };
  }

  const reportEdge = (blockId: string, edgeId: string, where: string) => {
    if (offEdges.has(`${blockId}.${edgeId}`)) diagnostics.push({ severity: "error", code: "INACTIVE_REFERENCE", message: `${where} uses edge ${edgeId}, which is off for these options.` });
  };
  const reportQualifiedEdge = (edgeId: string, where: string) => {
    if (offEdges.has(edgeId)) diagnostics.push({ severity: "error", code: "INACTIVE_REFERENCE", message: `${where} uses edge ${edgeId}, which is off for these options.` });
  };

  for (const [blockId, block] of Object.entries(blocks)) {
    const source = specification.blocks[blockId];
    const flatten = (list: EdgeListEntry[] | undefined, where: string): string[] => {
      const names = flattenEdgeList(list ?? [], source.outlineParts ?? {}, (when) => on(when, where), where, diagnostics);
      for (const name of names) reportEdge(blockId, name, where);
      return names;
    };
    for (const [edgeId, edge] of Object.entries(block.edges ?? {})) {
      for (const name of edge.edges ?? (edge.edge ? [edge.edge] : [])) reportEdge(blockId, name, `${blockId}.${edgeId}`);
    }
    for (const mark of block.marks ?? []) {
      if (mark.operation === "distributeButtons" && mark.along) reportEdge(blockId, mark.along, `${blockId}.${mark.id}`);
    }
    for (const [id, definition] of Object.entries(block.derivedValues ?? {})) {
      // Derived values name edges bare, across blocks.
      for (const name of definition.edges ?? []) {
        if ([...offEdges].some((edge) => edge.endsWith(`.${name}`)) && !Object.values(blocks).some((candidate) => candidate.edges?.[name])) {
          diagnostics.push({ severity: "error", code: "INACTIVE_REFERENCE", message: `${blockId}.${id} uses edge ${name}, which is off for these options.` });
        }
      }
    }
    const pieces: Record<string, PieceDefinition> = {};
    for (const [pieceId, piece] of Object.entries(block.pieces ?? {})) {
      const where = `${blockId}.${pieceId}`;
      const outline = flatten(piece.outline, where);
      if (piece.outline?.some((entry) => typeof entry !== "string")) checkContinuity(where, outline, block, diagnostics);
      if (piece.layoutWith && offPieces.has(`${blockId}.${piece.layoutWith}`)) {
        diagnostics.push({ severity: "error", code: "INACTIVE_REFERENCE", message: `${where} lays out with piece ${piece.layoutWith}, which is off for these options.` });
      }
      const internalMarks = flattenMarkList(piece.internalMarks ?? [], (when) => on(when, where), source.markParts ?? {});
      for (const markId of internalMarks) {
        if (offMarks.has(`${blockId}.${markId}`)) diagnostics.push({ severity: "error", code: "INACTIVE_REFERENCE", message: `${where} uses mark ${markId}, which is off for these options.` });
      }
      const bodyOverlay = piece.bodyOverlay && on(piece.bodyOverlay.when, `${where} body overlay`)
        ? { ...piece.bodyOverlay, edges: flatten(piece.bodyOverlay.edges, `${where} body overlay`) }
        : undefined;
      pieces[pieceId] = {
        ...piece,
        outline,
        ...(piece.internalLines ? { internalLines: flatten(piece.internalLines, `${where} internal lines`) } : {}),
        ...(piece.internalMarks ? { internalMarks } : {}),
        ...(piece.seamAllowances ? { seamAllowances: piece.seamAllowances.filter((rule) => on(rule.when, `${where} seam allowance`)).map((rule) => ({ ...rule, edges: flatten(rule.edges, `${where} seam allowance`) })) } : {}),
        ...(piece.inlays ? { inlays: piece.inlays.filter((rule) => on(rule.when, `${where} inlay`)).map((rule) => ({ ...rule, edges: flatten(rule.edges, `${where} inlay`) })) } : {}),
        ...(piece.clips ? { clips: piece.clips.filter((clip) => on(clip.when, `${where} clip`)).map((clip) => ({ ...clip, along: flatten(clip.along, `${where} clip`) })) } : {}),
        ...(piece.referenceOverlays ? { referenceOverlays: piece.referenceOverlays.filter((rule) => on(rule.when, `${where}.${rule.id}`)).map((rule) => ({ ...rule, edges: flatten(rule.edges, `${where}.${rule.id}`) })) } : {}),
        bodyOverlay,
      };
      if (!bodyOverlay) delete pieces[pieceId].bodyOverlay;
    }
    if (block.pieces) block.pieces = pieces;
  }

  const seamComparisons = specification.seamComparisons && Object.fromEntries(Object.entries(specification.seamComparisons).filter(([id, comparison]) => {
    if (!on(comparison?.when, `Seam comparison ${id}`)) return false;
    for (const edgeId of [...(comparison.first?.edges ?? []), ...(comparison.second?.edges ?? [])]) reportQualifiedEdge(edgeId, `Seam comparison ${id}`);
    return true;
  }));
  const assertions = specification.assertions?.filter((assertion) => {
    if (!on(assertion?.when, `assertion ${assertion?.id}`)) return false;
    if (assertion.check === "edgeLength") for (const edgeId of assertion.edges ?? []) reportQualifiedEdge(edgeId, `assertion ${assertion.id}`);
    return true;
  });
  return {
    ...specification,
    blocks,
    ...(seamComparisons ? { seamComparisons } : {}),
    ...(assertions ? { assertions } : {}),
  };
}

/** Flatten an edge list: groups whose `when` is on, and outline parts, expanded in order. */
export function flattenEdgeList(
  list: EdgeListEntry[],
  parts: Record<string, EdgeListEntry[]>,
  on: (when: Condition) => boolean,
  where: string,
  diagnostics: Diagnostic[],
  depth = 0,
): string[] {
  if (!Array.isArray(list)) return [];
  return list.flatMap((entry): string[] => {
    if (typeof entry === "string") return [entry];
    if (!entry || typeof entry !== "object") return [];
    if (depth >= MAX_PART_DEPTH) {
      diagnostics.push({ severity: "error", code: "OUTLINE_PART_DEPTH", message: `${where} nests outline parts or groups too deeply.` });
      return [];
    }
    if ("part" in entry) {
      const part = parts[entry.part];
      if (!part) {
        diagnostics.push({ severity: "warning", code: "UNKNOWN_PART", message: `${where} uses unknown outline part ${entry.part}.` });
        return [];
      }
      return flattenEdgeList(part, parts, on, where, diagnostics, depth + 1);
    }
    return on(entry.when) ? flattenEdgeList(entry.edges, parts, on, where, diagnostics, depth + 1) : [];
  });
}

/** Flatten a mark list: groups whose `when` is on, and mark parts, expanded in order. */
export function flattenMarkList(list: MarkListEntry[], on: (when: Condition) => boolean, parts: Record<string, MarkListEntry[]> = {}, depth = 0): string[] {
  if (!Array.isArray(list) || depth > MAX_PART_DEPTH) return [];
  return list.flatMap((entry): string[] => {
    if (typeof entry === "string") return [entry];
    if (!entry || typeof entry !== "object") return [];
    if ("part" in entry) return flattenMarkList(parts[entry.part] ?? [], on, parts, depth + 1);
    return on(entry.when) ? flattenMarkList(entry.marks, on, parts, depth + 1) : [];
  });
}

/** A conditional outline must still chain end to end for the options in use. */
function checkContinuity(where: string, outline: string[], block: BlockDefinition, diagnostics: Diagnostic[]) {
  const edges = outline.map((edgeId) => block.edges?.[edgeId]).filter((edge) => edge?.from && edge?.to);
  if (edges.length !== outline.length || edges.length < 2) return;
  for (let index = 0; index < edges.length; index += 1) {
    const current = edges[index]!;
    const next = edges[(index + 1) % edges.length]!;
    if (![current.from, current.to].some((id) => id === next.from || id === next.to)) {
      diagnostics.push({ severity: "warning", code: "OUTLINE_GAP", message: `${where} outline does not join ${outline[index]} to ${outline[(index + 1) % outline.length]} for these options.` });
    }
  }
}
